import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  AlertOctagon,
  ArrowLeft,
  CheckCircle,
  ChevronRight,
  Clock,
  Loader2,
  RefreshCw,
  Timer,
} from 'lucide-react';
import type {
  InterventionPriorite,
  InterventionStatut,
  InterventionTypeDemande,
} from '@mygmao/shared';
import { api, extractErrorMessage } from '../lib/api';
import { usePermissions } from '../hooks/usePermissions';
import { InterventionDetailDrawer } from '../components/interventions/InterventionDetailDrawer';
import {
  PrioriteBadge,
  StatutInterventionBadge,
  formatDuration,
  typeDemandeLabel,
} from '../components/interventions/badges';

/**
 * Page `/interventions/urgences` (US-INT-08).
 *
 *  - Liste P0/P1 actives avec calcul SLA côté serveur.
 *  - Auto-refresh toutes les 30 secondes.
 *  - Cartes colorées selon `sla_status` (ok / warning / breach).
 */

type SlaStatus = 'ok' | 'warning' | 'breach';

interface UrgenceRow {
  id: string;
  numero_demande: string;
  titre: string;
  type_demande: InterventionTypeDemande;
  priorite: InterventionPriorite;
  statut: InterventionStatut;
  date_demande: string;
  date_planifiee: string | null;
  duree_estimee: number | null;
  age_minutes: number;
  sla_threshold_hours: number;
  sla_status: SlaStatus;
  demandeur: { id: string; first_name: string; last_name: string } | null;
  service_demandeur: { id: string; nom_complet: string } | null;
  equipement: { id: string; code_equipement: string; designation: string } | null;
  technicien: { id: string; first_name: string; last_name: string } | null;
}

export function UrgencesPage() {
  const { can } = usePermissions();
  const canRead = can('interventions', 'read');

  const [items, setItems] = useState<UrgenceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  async function refresh() {
    setLoading(true);
    setErrorMessage(null);
    try {
      const res = await api.get<{ data: UrgenceRow[] }>('/interventions/urgences');
      setItems(res.data.data);
      setLastRefresh(new Date());
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!canRead) return;
    refresh();
    const t = setInterval(refresh, 30_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRead]);

  if (!canRead) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <p className="text-sm text-gray-500">Vous n'avez pas accès au module Interventions.</p>
      </div>
    );
  }

  const breaches = items.filter((i) => i.sla_status === 'breach').length;

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <Link
            to="/interventions"
            className="inline-flex items-center text-xs text-gray-500 hover:text-gray-800 mb-1"
          >
            <ArrowLeft className="w-3 h-3 mr-1" />
            Retour aux interventions
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <AlertOctagon className="w-7 h-7 mr-2 text-red-600" />
            Urgences P0/P1
          </h1>
          <p className="text-xs text-gray-400 mt-1">
            Mis à jour à {lastRefresh.toLocaleTimeString()} — rafraîchissement automatique toutes
            les 30 s
          </p>
        </div>
        <button
          onClick={refresh}
          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4 mr-1.5" />
          Rafraîchir
        </button>
      </div>

      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      {/* Bannière */}
      {items.length > 0 && (
        <div className="rounded-xl border border-red-300 bg-red-50 p-4">
          <p className="text-sm font-bold text-red-900 flex items-center">
            <AlertOctagon className="w-4 h-4 mr-2" />
            {items.length} urgence{items.length > 1 ? 's' : ''} P0/P1 en cours
            {breaches > 0 && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-600 text-white">
                {breaches} SLA dépassé{breaches > 1 ? 's' : ''}
              </span>
            )}
          </p>
          <p className="text-xs text-red-800 mt-1">
            Action immédiate requise pour les interventions critiques.
          </p>
        </div>
      )}

      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center p-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-green-200 bg-green-50 p-8 text-center">
          <CheckCircle className="w-10 h-10 text-green-600 mx-auto mb-2" />
          <p className="text-sm font-medium text-green-900">Aucune urgence en cours.</p>
          <p className="text-xs text-green-700 mt-1">
            Toutes les interventions critiques ont été traitées.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((it) => (
            <UrgenceCard key={it.id} item={it} onOpen={() => setDetailId(it.id)} />
          ))}
        </div>
      )}

      {detailId && (
        <InterventionDetailDrawer
          interventionId={detailId}
          onClose={() => setDetailId(null)}
          onRefresh={refresh}
        />
      )}
    </div>
  );
}

function UrgenceCard({ item, onOpen }: { item: UrgenceRow; onOpen(): void }) {
  const slaClass: Record<SlaStatus, string> = {
    ok: 'border-green-200 bg-green-50',
    warning: 'border-amber-300 bg-amber-50',
    breach: 'border-red-300 bg-red-50 ring-2 ring-red-400 ring-offset-2',
  };
  const slaLabel: Record<SlaStatus, string> = {
    ok: 'SLA OK',
    warning: 'SLA proche',
    breach: 'SLA dépassé',
  };
  const slaBadge: Record<SlaStatus, string> = {
    ok: 'bg-green-100 text-green-800 border-green-200',
    warning: 'bg-amber-100 text-amber-800 border-amber-300',
    breach: 'bg-red-100 text-red-800 border-red-300',
  };

  return (
    <div className={`rounded-xl border p-4 ${slaClass[item.sla_status]}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <p className="text-xs font-semibold text-primary-600 uppercase tracking-wide">
            {item.numero_demande}
          </p>
          <p className="text-sm font-semibold text-gray-900 truncate">{item.titre}</p>
        </div>
        <div className="flex items-center space-x-1 flex-shrink-0">
          <PrioriteBadge priorite={item.priorite} compact />
        </div>
      </div>

      <div className="flex items-center flex-wrap gap-2 mb-2">
        <StatutInterventionBadge statut={item.statut} />
        <span
          className={`inline-flex items-center text-xs px-1.5 py-0.5 rounded font-medium border ${slaBadge[item.sla_status]}`}
        >
          <Timer className="w-3 h-3 mr-1" />
          {slaLabel[item.sla_status]}
        </span>
      </div>

      <dl className="text-xs text-gray-700 space-y-0.5">
        <div className="flex justify-between">
          <dt className="text-gray-500">Type</dt>
          <dd>{typeDemandeLabel(item.type_demande)}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-gray-500">Demandeur</dt>
          <dd>
            {item.demandeur ? `${item.demandeur.first_name} ${item.demandeur.last_name}` : '—'}
          </dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-gray-500">Service</dt>
          <dd>{item.service_demandeur?.nom_complet ?? '—'}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-gray-500">Équipement</dt>
          <dd className="truncate max-w-[180px]">{item.equipement?.designation ?? '—'}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-gray-500">Technicien</dt>
          <dd>
            {item.technicien ? (
              `${item.technicien.first_name} ${item.technicien.last_name}`
            ) : (
              <span className="text-red-600">non affecté</span>
            )}
          </dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-gray-500 inline-flex items-center">
            <Clock className="w-3 h-3 mr-1" />
            Âge
          </dt>
          <dd className="font-semibold text-gray-900">
            {formatDuration(item.age_minutes)}
            <span className="text-gray-400 font-normal ml-1">
              / SLA {item.sla_threshold_hours}h
            </span>
          </dd>
        </div>
      </dl>

      <div className="mt-3 flex justify-end">
        <button
          onClick={onOpen}
          className="inline-flex items-center px-3 py-1.5 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg text-xs font-medium text-gray-700"
        >
          Voir le détail
          <ChevronRight className="w-3 h-3 ml-1" />
        </button>
      </div>
    </div>
  );
}
